import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import cn from "classnames";
import moment from "moment";
import { toast } from "react-toastify";
import { Trans, useTranslation } from "react-i18next";
import style from "./OrderStatusTimeline.module.scss";
import { Driver, Order } from "../../../../services";
import { numberToFinanceFormat } from "../../../../utils";
import { CURRENCIES, acceptstatus, rejectstatus } from "../../../../mock";
import CustomButton from "../../../custom/custom-button/CustomButton";
import CustomInput from "../../../custom/custom-input/CustomInput";
import CommentIcon from "../../../shared/icons/CommentIcon";
import ClockIcon from "../../../shared/icons/ClockIcon";
import AppTooltip from "../../../shared/AppTooltip/AppTooltip";
import BidChangeCard from "./BidChangeCard";
import { XIcon, InfoIcon } from "lucide-react";
import ChevronIcon from "../../../shared/icons/ChevronIcon";
import CheckIcon from "../../../shared/icons/CheckIcon";
import ReassignExpeditorCard from "./ReassignExpeditorCard";
import { getOrderWagonNumbers, isRailwayOrder } from "../../../../pages/orders/utils/railwayOrder";

// Сопоставление шага таймлайна с возможными statusCode из User.getHistory
const STEP_STATUS_CODES = {
  created: ["created"],
  tender: ["awaitingoffer"],
  rebidClosed: ["rebidclosed"],
  inRebid: ["inrebid"],
  assigned: ["assigned"],
  assignedexecutor: ["assignedexecutor"],
  confirmed: ["confirmed", "booked"],
  loading: ["headingtoloading", "processloadingcargo", "loadingcargo"],
  ontrip: ["onthetrip"],
  unloading: ["unloadingcargo", "processunloadingcargo", "unloadingcargomoderation"],
  execDone: ["completedbyexecutor"],
  expeditorRejected: ["expeditorrejected"],
  return: ["return"],
  returnToTender: ["returntotender"],
  completed: ["completed"],
  canceled: ["canceled"],
};

const CODE_TO_STEP = Object.entries(STEP_STATUS_CODES).reduce((acc, [step, codes]) => {
  codes.forEach((code) => {
    acc[code] = step;
  });
  return acc;
}, {});

const STATUS_CONTENT_OVERRIDES = {
  headingtoloading: {
    titleKey: "orderInfoClient.timeline.stepTitles.headingtoloading",
    bodyKey: "orderInfoClient.timeline.headingToLoadingText",
  },
  unloadingcargomoderation: {
    titleKey: "orderInfoClient.timeline.stepTitles.unloadingcargomoderation",
    bodyKey: "orderInfoClient.timeline.unloadingCargoModerationText",
  },
};

const hasValue = (v) => v !== undefined && v !== null && v !== "";
const EMPTY_VALUE = "-";
const fmtDateTime = (v) => (v ? moment(v).format("DD.MM.YYYY, HH:mm") : EMPTY_VALUE);
const fmtDate = (v) => (v ? moment(v).format("DD.MM.YYYY") : EMPTY_VALUE);
const fmtTrackingDate = (v) => (v ? moment(v).format("DD.MM.YYYY (HH:mm)") : EMPTY_VALUE);
const fmtBidDate = (v) => (v ? moment(v).format("DD.MM.YYYY (HH:mm)") : EMPTY_VALUE);
const ADDRESS_TYPES = {
  loading: "Loading",
  unloading: "Unloading",
};
const getWarehouseTypeKey = (type, fallback = "unloading") => {
  if (type === ADDRESS_TYPES.loading) return "loading";
  if (type === ADDRESS_TYPES.unloading) return "unloading";
  return fallback;
};

const getStepTitleKey = (key, contentOverride, assignmentMethod) => {
  if (contentOverride?.titleKey) return contentOverride.titleKey;
  if (assignmentMethod === "Auction" && key === "created") {
    return "orderInfoClient.timeline.stepTitles.createdAuction";
  }
  if (assignmentMethod === "Auction" && key === "tender") {
    return "orderInfoClient.timeline.stepTitles.auction";
  }
  if (assignmentMethod === "Auction" && key === "returnToTender") {
    return "orderInfoClient.timeline.stepTitles.returnToAuction";
  }
  return `orderInfoClient.timeline.stepTitles.${key}`;
};

const OrderStatusTimeline = ({ order, statusHistory = [], bids = [], fetchOrderData, onForceUpdate }) => {
  const { t } = useTranslation();
  const isRailway = isRailwayOrder(order);
  const wagonNumbers = isRailway ? getOrderWagonNumbers(order) : [];
  const [openMap, setOpenMap] = useState({});
  const [activeBidComment, setActiveBidComment] = useState(null);
  const [driversData, setDriversData] = useState({});
  const [trackingPoints, setTrackingPoints] = useState([]);

  const toggleBidComment = (id) => setActiveBidComment((prev) => (prev === id ? null : id));

  // данные для блока "Завершён от исполнителя" (логика из OrderDecisionClient)
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [commentError, setCommentError] = useState("");

  const drivers = useMemo(() => order?.drivers || [], [order]);
  const driverId = drivers[0]?.driverId;

  // Загружаем данные всех водителей/ТС (как в ExecutorTab) — map по driverId.
  useEffect(() => {
    if (!drivers.length) {
      setDriversData({});
      return;
    }
    drivers.forEach(({ driverId: dId, vehicleId }) => {
      setDriversData((prev) => ({
        ...prev,
        [dId]: prev[dId] || { info: null, vehicleInfo: null },
      }));

      Driver.getDriverInfo(dId)
        .then((r) => {
          if (r.status === 200) {
            setDriversData((prev) => ({ ...prev, [dId]: { ...prev[dId], info: r.data || {} } }));
          }
        })
        .catch(() => {});

      if (vehicleId) {
        Driver.getVehicleInfo(vehicleId)
          .then((r) => {
            if (r.status === 200) {
              setDriversData((prev) => ({ ...prev, [dId]: { ...prev[dId], vehicleInfo: r.data || {} } }));
            }
          })
          .catch(() => {});
      }
    });
  }, [drivers]);

  const getDriverName = (info) =>
    info ? [info.surname, info.name, info.patronymic].filter(Boolean).join(" ") : EMPTY_VALUE;

  // Первый водитель — для одиночных текстов (loading/ontrip/unloading) и одиночного confirmed.
  const driverInfo = driversData[driverId]?.info || null;
  const driverName = getDriverName(driverInfo);

  const expeditorName = order?.expeditorName || t("orderInfoClient.timeline.transportCompanyFallback");
  const isWithoutExecutor = Boolean(order?.isWithoutExecutor);
  const timelineActorPrefix = isWithoutExecutor
    ? t("orderInfoClient.timeline.executorPrefix")
    : t("orderInfoClient.timeline.loadingPrefix");
  const timelineActorName = isWithoutExecutor ? expeditorName : driverName;

  const routeAddresses = useMemo(
    () =>
      (order?.addressList || [])
        .slice()
        .sort((a, b) => (a.step ?? 0) - (b.step ?? 0)),
    [order],
  );

  const blocks = useMemo(() => {
    const fromHistory = (statusHistory || [])
      .slice()
      .sort((a, b) => (a.createdAt && b.createdAt ? new Date(a.createdAt) - new Date(b.createdAt) : 0))
      .map((h) => {
        const code = (h.statusCode || "").toLowerCase();
        if (code === "pricechanged") {
          return { key: "priceChanged", time: h.createdAt, comment: h.comment, priceItem: h };
        }
        return {
          key: CODE_TO_STEP[code],
          statusCode: code,
          time: h.createdAt,
          comment: h.comment,
          moderator: h.moderator,
          expeditorName: h.expeditorName,
        };
      })
      .filter((b) => b.key);

    if (!fromHistory.some((b) => b.key === "created")) {
      fromHistory.unshift({ key: "created", time: order?.createdAt });
    }

    const tenderFinished = (bids || []).some((b) => b?.isAccepted);
    const POST_TENDER_STEPS = [
      "assignedexecutor",
      "confirmed",
      "loading",
      "ontrip",
      "unloading",
      "execDone",
      "completed",
      "return",
      "expeditorRejected",
    ];
    const reachedExecutor = fromHistory.some((b) => POST_TENDER_STEPS.includes(b.key));
    if (tenderFinished && !reachedExecutor) {
      fromHistory.push({ key: "assignedexecutor", time: null, pending: true });
    }
    return fromHistory;
  }, [statusHistory, order, bids]);

  const lastReturnIdx = blocks.map((b) => b.key).lastIndexOf("return");
  const lastStatusBlockIdx = blocks.reduce(
    (lastIndex, block, index) => (block.key === "priceChanged" ? lastIndex : index),
    -1,
  );

  const hasOntrip = useMemo(() => blocks.some((b) => b.key === "ontrip"), [blocks]);

  useEffect(() => {
    if (!order?.id || !hasOntrip) {
      setTrackingPoints([]);
      return;
    }
    Order.getOrderTrackingPoints(order.id)
      .then((r) => setTrackingPoints(Array.isArray(r?.data) ? r.data : []))
      .catch(() => setTrackingPoints([]));
  }, [order?.id, hasOntrip]);

  const sortedTrackingPoints = useMemo(
    () => [...trackingPoints].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [trackingPoints],
  );

  // Решение по изменению ставки недоступно, если заказ в одном из финальных статусов.
  const BID_DECISION_BLOCKED = ["completed", "canceled"];
  const currentOrderStatusCode = useMemo(() => {
    const codes = blocks.map((block) => block.statusCode).filter(Boolean);
    return codes[codes.length - 1] || "";
  }, [blocks]);
  const canDecideBid = !BID_DECISION_BLOCKED.includes(currentOrderStatusCode);

  const isOpen = (i, isCurrent) => (i in openMap ? openMap[i] : isCurrent);
  const toggle = (i, isCurrent) =>
    setOpenMap((prev) => ({ ...prev, [i]: !(i in prev ? prev[i] : isCurrent) }));

  // --- логика решения клиента (из OrderDecisionClient) ---
  const handleAccept = () => {
    if (!order?.id) return;
    setDecisionLoading(true);
    const next = acceptstatus(order.status);
    Order.updateStatus(order.id, next)
      .then(() => {
        toast.success(t("orderInfoClient.timeline.confirmSuccess"));
        fetchOrderData?.();
        onForceUpdate?.();
      })
      .catch(() => toast.error(t("orderInfoClient.timeline.updateStatusError")))
      .finally(() => setDecisionLoading(false));
  };

  const handleRejectSubmit = () => {
    if (!rejectComment.trim()) {
      setCommentError(t("orderInfoClient.timeline.returnReasonRequired"));
      return;
    }
    setDecisionLoading(true);
    const next = rejectstatus(order.status);
    Order.updateStatus(order.id, next, rejectComment.trim())
      .then(() => {
        setShowRejectModal(false);
        setRejectComment("");
        toast.success(t("orderInfoClient.timeline.returnSuccess"));
        fetchOrderData?.();
        onForceUpdate?.();
      })
      .catch(() => toast.error(t("orderInfoClient.timeline.updateStatusError")))
      .finally(() => setDecisionLoading(false));
  };

  const renderBody = (key, isCurrent, isLastReturn, rejectedTime, returnComment, blockIndex, moderator, statusCode) => {
    // История назначения относится к своей ТК, а не к текущему исполнителю заказа.
    const nextAssignmentIndex = blocks.findIndex((block, index) => index > blockIndex && block.key === "assigned");
    const nextRejection = blocks
      .slice(blockIndex + 1, nextAssignmentIndex < 0 ? undefined : nextAssignmentIndex)
      .find((block) => block.key === "expeditorRejected");
    const historicalExpeditorName =
      blocks[blockIndex]?.expeditorName ||
      (key === "expeditorRejected" ? moderator : nextRejection?.expeditorName || nextRejection?.moderator) ||
      (nextAssignmentIndex < 0 ? expeditorName : t("orderInfoClient.timeline.transportCompanyFallback"));
    switch (key) {
      case "created":
        return (
          <div className={style.created_row}>
            <span className={style.created_item}>
              <span>{t("orderInfoClient.timeline.createdBy")} </span>
              <b>{order?.author || EMPTY_VALUE}</b>
            </span>
            <span className={style.created_item}>
              <span>{t("orderInfoClient.timeline.createdAt")} </span>
              <b>{fmtDate(order?.createdAt)}</b>
            </span>
          </div>
        );

      case "rebidClosed":
        return <p className={style.text}>{t("orderInfoClient.timeline.rebidClosedText")}</p>;

      case "inRebid":
        return (
          <p className={style.text}>
            {t("orderInfoClient.timeline.inRebidText", { date: fmtDateTime(order?.tenderDateEnd) })}
          </p>
        );

      case "tender": {
        const isDistributed = Boolean(order?.isComplexTender) || bids.some((b) => !!b?.vehiclesCount);
        const sortedBids = order?.expeditorId
          ? [...bids].sort(
              (a, b) =>
                (b?.expeditorId === order.expeditorId ? 1 : 0) - (a?.expeditorId === order.expeditorId ? 1 : 0),
            )
          : bids;
        const acceptedBid = sortedBids.find((bid) => bid?.isAccepted);
        const acceptedByName = acceptedBid?.acceptedBy || EMPTY_VALUE;
        const acceptedByTooltipId = `timeline-tender-accepted-by-${acceptedBid?.id || "accepted"}`;
        const shouldShowAcceptedByTooltip = acceptedByName !== EMPTY_VALUE;
        const acceptedBidDate = fmtDateTime(acceptedBid?.acceptedAt);
        return (
          <div className={cn(style.bids, { [style.bids_railway]: isRailway })}>
            <div className={style.bids_head}>
              <span className={style.bids_col_head}>
                {t(`orderInfoClient.suggestedPrices.${isRailway ? "columnCompanyAndDate" : "columnName"}`)}
              </span>
              {!isRailway && <span className={style.bids_col_head}>{t("orderInfoClient.suggestedPrices.columnDate")}</span>}
              <span className={style.bids_col_head}>
                {t(`orderInfoClient.suggestedPrices.${isRailway ? "columnBid" : "columnAmount"}`)}
              </span>
              <span className={style.bids_col_head}>
                {t(`orderInfoClient.suggestedPrices.${isRailway ? "columnTerm" : "columnDeliveryTerm"}`)}
              </span>
              {isRailway && (
                <span className={style.bids_col_head}>{t("orderInfoClient.suggestedPrices.columnWagons")}</span>
              )}
              <span className={style.bids_col_head}>
                {t(`orderInfoClient.suggestedPrices.${isDistributed ? "columnDistributed" : "columnStatus"}`)}
              </span>
            </div>
            {sortedBids.map((bid, index) => {
              const hasCommentDetails = bid?.comments || hasValue(bid?.weight) || hasValue(bid?.volume);
              const bidName = bid.expeditorName || bid.userName || EMPTY_VALUE;
              const bidNameTooltipId = `timeline-bid-name-tooltip-${bid.id || index}`;
              const isOrderExecutor =
                isDistributed && order?.expeditorId && bid?.expeditorId === order.expeditorId;
              const bidStatus = (
                <span className={bid?.isAccepted ? style.badge_accepted : style.badge_rejected}>
                  {t(`orderInfoClient.suggestedPrices.${bid?.isAccepted ? "acceptedStatus" : "notAcceptedStatus"}`)}
                </span>
              );

              return (
                <React.Fragment key={bid.id}>
                  <div className={cn(style.bids_row, { [style.bids_row_executor]: isOrderExecutor })}>
                    {isRailway ? (
                      <div className={style.bids_company}>
                        <span className={cn(style.bids_cell, style.bids_cell_name)} data-tooltip-id={bidNameTooltipId}>
                          {bidName}
                        </span>
                        <span className={cn(style.bids_cell, style.bids_date)}>
                          {fmtDateTime(bid.updatedAt || bid.createdAt)}
                        </span>
                      </div>
                    ) : (
                      <>
                        <span className={cn(style.bids_cell, style.bids_cell_name)} data-tooltip-id={bidNameTooltipId}>
                          {bidName}
                        </span>
                        <span className={style.bids_cell}>{fmtBidDate(bid.updatedAt || bid.createdAt)}</span>
                      </>
                    )}
                    <span
                      className={style.bids_cell}
                      data-label={isRailway ? t("orderInfoClient.suggestedPrices.columnBid") : undefined}
                    >
                      {numberToFinanceFormat(bid.price)} {CURRENCIES[bid?.currency || "KZT"]?.symbol || ""}
                    </span>
                    <span
                      className={cn(style.bids_cell, style.bids_term)}
                      data-label={isRailway ? t("orderInfoClient.suggestedPrices.columnTerm") : undefined}
                    >
                      {bid?.plannedDays ? t("orderInfoClient.suggestedPrices.days", { count: bid.plannedDays }) : EMPTY_VALUE}
                      {!isDistributed && hasCommentDetails && (
                        <button
                          className={style.comment_btn}
                          onClick={() => toggleBidComment(bid.id)}
                          title={t("orderInfoClient.suggestedPrices.commentTitle")}
                        >
                          <CommentIcon type={3} fill="#212121" />
                        </button>
                      )}
                    </span>
                    {isRailway && (
                      <span className={style.bids_cell} data-label={t("orderInfoClient.suggestedPrices.columnWagons")}>
                        {isDistributed
                          ? bid?.vehiclesCount ?? 0
                          : hasValue(bid?.wagonsCount) ? bid.wagonsCount : EMPTY_VALUE}
                      </span>
                    )}
                    {isDistributed ? (
                      <span
                        className={cn(style.bids_cell, { [style.bids_status]: isRailway })}
                        data-label={isRailway ? t("orderInfoClient.suggestedPrices.columnDistributed") : undefined}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                      >
                        {t("orderInfoClient.suggestedPrices.distributedCount", {
                          accepted: bid?.acceptedVehiclesCount ?? 0,
                          total: bid?.vehiclesCount ?? 0,
                        })}
                        {hasCommentDetails && (
                          <button
                            className={style.comment_btn}
                            onClick={() => toggleBidComment(bid.id)}
                            title={t("orderInfoClient.suggestedPrices.commentTitle")}
                          >
                            <CommentIcon type={3} fill="#212121" />
                          </button>
                        )}
                      </span>
                    ) : isRailway ? (
                      <div className={style.bids_status} data-label={t("orderInfoClient.suggestedPrices.columnStatus")}>
                        {bidStatus}
                      </div>
                    ) : (
                      bidStatus
                    )}
                  </div>
                  {isOrderExecutor && (
                    <div className={style.executor_note}>
                      <InfoIcon size={16} color="#22659A" />
                      <span>{t("orderInfoClient.suggestedPrices.partnerExecutesOrder")}</span>
                    </div>
                  )}
                  <AppTooltip id={bidNameTooltipId} place="top" positionStrategy="fixed">
                    {bidName}
                  </AppTooltip>

                  {activeBidComment === bid.id && hasCommentDetails && (
                    <div className={style.comment_box}>
                      <div className={style.comment_box_head}>{t("orderInfoClient.suggestedPrices.commentTitle")}</div>
                      {bid?.comments && <div className={style.comment_box_text}>{bid.comments}</div>}
                      {hasValue(bid?.weight) && (
                        <div className={style.comment_box_text}>
                          <b>{t("orderInfoClient.suggestedPrices.weight")}</b> {bid.weight}
                        </div>
                      )}
                      {hasValue(bid?.volume) && (
                        <div className={style.comment_box_text}>
                          <b>{t("orderInfoClient.suggestedPrices.volume")}</b> {bid.volume}
                        </div>
                      )}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
            {acceptedBid && (
              <div className={style.tender_decision_footer}>
                <span className={style.tender_decision_item}>
                  <span className={style.tender_decision_label}>
                    {t("orderInfoAviation.actions.decisionAcceptedBy")}
                  </span>
                  <span
                    className={cn(style.tender_decision_value, style.tender_decision_name)}
                    data-tooltip-id={shouldShowAcceptedByTooltip ? acceptedByTooltipId : undefined}
                  >
                    {acceptedByName}
                  </span>
                </span>
                <span className={style.tender_decision_item}>
                  <span className={style.tender_decision_label}>
                    {t("orderInfoAviation.actions.acceptedAt")}
                  </span>
                  <span className={style.tender_decision_value}>{acceptedBidDate}</span>
                </span>
                {shouldShowAcceptedByTooltip && (
                  <AppTooltip id={acceptedByTooltipId} place="top" positionStrategy="fixed">
                    {acceptedByName}
                  </AppTooltip>
                )}
              </div>
            )}
          </div>
        );
      }

      case "assigned":
        return (
          <>
            <p className={style.text}>
              {t("orderInfoClient.timeline.assignedDecisionPrefix")} <b>{historicalExpeditorName}</b>
            </p>
            {moderator && (
              <p className={style.text}>
                {t("orderInfoClient.timeline.reassignment.assignedBy", {
                  employee: moderator,
                  company: historicalExpeditorName,
                })}
              </p>
            )}
          </>
        );
      
      case "assignedexecutor":
        return (
          <p className={style.text}>
            {t("orderInfoClient.timeline.assignedPrefix")} <b>{expeditorName}</b>.
          </p>
        );


      case "confirmed": {
        const wagonNumbersDetails =
          statusCode === "confirmed" && wagonNumbers.length > 0 ? (
            <div className={style.wagon_fields}>
              {wagonNumbers.map((wagonNumber, index) => (
                <div className={style.field} key={`${wagonNumber}-${index}`}>
                  <span className={style.field_label}>
                    {t("railwayOrder.wagonNumber")} {index + 1}
                  </span>
                  <span className={style.field_value}>{wagonNumber}</span>
                </div>
              ))}
            </div>
          ) : null;
        // Для заказа без исполнителя данные ТС/водителя не показываем.
        if (order?.isWithoutExecutor) return wagonNumbersDetails;
        const multiple = drivers.length > 1;
        // Рендер полей одного водителя. Ожидаемая дата погрузки — только у первого.
        const renderDriverFields = (driverIndex) => {
          const d = drivers[driverIndex] || {};
          const data = driversData[d.driverId] || {};
          const vInfo = data.vehicleInfo;
          const name = getDriverName(data.info);
          const driverLabel = multiple
            ? `${t("orderInfoClient.timeline.assignedDriver")} ${driverIndex + 1}`
            : t("orderInfoClient.timeline.assignedDriver");
          return (
            <>
              <div className={style.field}>
                <span className={style.field_label}>{driverLabel}</span>
                <span className={style.field_value}>{name}</span>
              </div>
              <div className={style.field}>
                <span className={style.field_label}>{t("orderInfoClient.timeline.carModel")}</span>
                <span className={style.field_value}>{vInfo?.carModel || EMPTY_VALUE}</span>
              </div>
              <div className={style.field}>
                <span className={style.field_label}>{t("orderInfoClient.timeline.carNumber")}</span>
                <span className={style.field_value}>{vInfo?.carNumber || EMPTY_VALUE}</span>
              </div>
              {driverIndex === 0 ? (
                <div className={style.field}>
                  <span className={style.field_label}>
                    {t("orderInfoClient.timeline.expectedLoadingDate")}
                  </span>
                  <span className={style.field_value}>{fmtDateTime(order?.shippingDate)}</span>
                </div>
              ) : (
                <div className={style.field}></div>
              )}
              <div className={style.field}>
                <span className={style.field_label}>{t("orderInfoClient.timeline.trailerModel")}</span>
                <span className={style.field_value}>{vInfo?.trailerModel || EMPTY_VALUE}</span>
              </div>
              <div className={style.field}>
                <span className={style.field_label}>{t("orderInfoClient.timeline.trailerNumber")}</span>
                <span className={style.field_value}>{vInfo?.trailerNumber || EMPTY_VALUE}</span>
              </div>
            </>
          );
        };

        if (!multiple) {
          return (
            <>
              {wagonNumbersDetails}
              <div className={style.fields}>{renderDriverFields(0)}</div>
            </>
          );
        }

        return (
          <>
            {wagonNumbersDetails}
            {drivers.map((d, idx) => (
              <div
                key={d.driverId || idx}
                className={style.fields}
                style={
                  idx < drivers.length - 1
                    ? { borderBottom: "0.55px solid #E6E0EC", paddingBottom: 12, marginBottom: 12 }
                    : undefined
                }
              >
                {renderDriverFields(idx)}
              </div>
            ))}
          </>
        );
      }

      case "loading":
        if (statusCode === "headingtoloading") {
          return <p className={style.text}>{t("orderInfoClient.timeline.headingToLoadingText")}</p>;
        }

        return (
          <p className={style.text}>
            {timelineActorPrefix} <b>{timelineActorName}</b>{" "}
            {t(isRailway ? "orderInfoClient.timeline.loadingSuffixRailway" : "orderInfoClient.timeline.loadingSuffix")}
          </p>
        );

      case "ontrip": {
        // N-й статус "В пути" ведёт к следующей точке маршрута: точка №(N+1),
        // т.к. отсчёт начинается от исходной точки погрузки (index 0).
        const ontripOrdinal = blocks
          .slice(0, (blockIndex ?? 0) + 1)
          .filter((b) => b.key === "ontrip").length;
        const fallbackDest = routeAddresses[ontripOrdinal] || routeAddresses[routeAddresses.length - 1];
        const originIndex = routeAddresses.reduce(
          (lastIndex, address, index) => (address?.isCompleted ? index : lastIndex),
          -1,
        );
        const origin = routeAddresses[originIndex] || routeAddresses[ontripOrdinal - 1];
        const dest =
          routeAddresses.find((address, index) => index > originIndex && !address?.isCompleted) ||
          routeAddresses.find((address) => !address?.isCompleted) ||
          fallbackDest;
        const destAddress = dest?.address || dest?.name || order?.cityToName || EMPTY_VALUE;
        const destArrival = dest?.expectedUnloadingDate || dest?.shippingDate;
        const originWarehouseType = t(
          `orderInfoClient.timeline.warehouseTypes.${getWarehouseTypeKey(origin?.type, "loading")}`,
        );
        const destinationWarehouseType = t(
          `orderInfoClient.timeline.warehouseTypes.${getWarehouseTypeKey(dest?.type)}`,
        );
        return (
          <>
            <p className={style.text}>
              <Trans
                i18nKey={
                  isRailway ? "orderInfoClient.timeline.onTripTextRailway" : "orderInfoClient.timeline.onTripText"
                }
                values={{ driverName: timelineActorName, expeditorName, originWarehouseType, destinationWarehouseType }}
                components={{ driver: <b /> }}
              />
            </p>
            <div className={style.fields} style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
              <div className={style.field}>
                <span className={style.field_label}>{t("orderInfoClient.timeline.unloadingAddress")}</span>
                <span className={style.field_value}>{destAddress}</span>
              </div>
              <div className={style.field}>
                <span className={style.field_label}>{t("orderInfoClient.timeline.estimatedArrivalDate")}</span>
                <span className={style.field_value}>{fmtDate(destArrival)}</span>
              </div>
            </div>
            {sortedTrackingPoints.length > 0 && (
              <div className={style.tracking}>
                <div className={style.tracking_row}>
                  <span className={style.tracking_label}>{t("orderInfoClient.suggestedPrices.columnDate")}:</span>
                  <span className={style.tracking_label}>{t("orderInfoClient.timeline.cargoLocation")}:</span>
                </div>
                {sortedTrackingPoints.map((point) => (
                  <div key={point.id} className={style.tracking_row}>
                    <span className={style.tracking_value}>{fmtTrackingDate(point.createdAt)}</span>
                    <span className={style.tracking_value}>{point.location || EMPTY_VALUE}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        );
      }

      case "unloading":
        if (statusCode === "unloadingcargomoderation") {
          return <p className={style.text}>{t("orderInfoClient.timeline.unloadingCargoModerationText")}</p>;
        }

        return (
          <p className={style.text}>
            {timelineActorPrefix} <b>{timelineActorName}</b> {t("orderInfoClient.timeline.unloadingSuffix")}
          </p>
        );

      case "execDone":
        return (
          <>
            <p className={style.text}>
              <b>{expeditorName}</b> {t("orderInfoClient.timeline.execDoneSuffix")}
              {isCurrent ? `. ${t("orderInfoClient.timeline.execDoneCurrentSuffix")}` : "."}
            </p>
            {isCurrent && (
              <div className={style.decision_actions}>
                <CustomButton
                  text={t("orderInfoClient.timeline.confirm")}
                  isNavy
                  style={{ flex: 1 }}
                  onClick={handleAccept}
                  isLoading={decisionLoading}
                  disabled={decisionLoading}
                />
                <CustomButton
                  text={t("orderInfoClient.timeline.reject")}
                  isNavy
                  isWhite
                  style={{ flex: 1 }}
                  onClick={() => {
                    setRejectComment("");
                    setCommentError("");
                    setShowRejectModal(true);
                  }}
                  disabled={decisionLoading}
                />
              </div>
            )}
          </>
        );

      case "return":
        return (
          <>
            <p className={style.text}>{t("orderInfoClient.timeline.returnedText")}</p>
            {(returnComment || isLastReturn) && (
              <div className={style.comment_box}>
                <div className={style.comment_box_head}>
                  <span>{t("orderInfoClient.timeline.yourComment")}</span>
                </div>
                <div className={style.comment_box_text}>{returnComment || order?.rejectReason || EMPTY_VALUE}</div>
              </div>
            )}
          </>
        );

      case "returnToTender":
        return (
          <>
            {returnComment && (
              <p className={style.text}>
                {t("orderInfoClient.timeline.returnToTenderReason")} {returnComment}
              </p>
            )}
          </>
        );

      case "completed":
        return (
          <div className={style.created_row}>
            <span className={style.created_item}>
              <b>{t("orderInfoClient.timeline.completedText")}</b>
            </span>
            <span className={style.created_item}>
              <span>{t("orderInfoClient.timeline.completedAt")} </span>
              <b>{fmtDateTime(order?.updatedAt)}</b>
            </span>
          </div>
        );

      case "expeditorRejected":
        return (
          <p className={style.text}>
            {t("orderInfoClient.timeline.expeditorRejectedPrefix")} <b>{historicalExpeditorName}</b>{" "}
            {t("orderInfoClient.timeline.expeditorRejectedSuffix")}{" "}
            {rejectedTime ? fmtDateTime(rejectedTime) : EMPTY_VALUE}
          </p>
        );

      case "canceled":
        return (
          <>
            <div className={style.created_row}>
              <span className={style.created_item}>
                <span>{t("orderInfoClient.timeline.canceledBy")} </span>
                <b>{moderator || EMPTY_VALUE}</b>
              </span>
              <span className={style.created_item}>
                <span>{t("orderInfoClient.timeline.canceledAt")} </span>
                <b>{fmtDateTime(rejectedTime)}</b>
              </span>
            </div>
            {returnComment && (
              <div className={style.comment_box}>
                <div className={style.comment_box_head}>
                  <span>{t("orderInfoClient.timeline.cancelReason")}</span>
                </div>
                <div className={style.comment_box_text}>{returnComment}</div>
              </div>
            )}
          </>
        );

      default:
        return null;
    }
  };

  if (!order) return null;

  return (
    <div className={style.timeline}>
      {blocks.map(({ key, statusCode, time, pending, comment, priceItem, moderator }, i) => {
        const isLast = i === blocks.length - 1;

        if (key === "priceChanged") {
          const priceStatus = priceItem?.changedPriceStatus;
          const circleCls =
            priceStatus === "confirmed"
              ? style.circle_done
              : priceStatus === "rejected"
              ? style.circle_priceRejected
              : style.circle_current;
          const circleIcon =
            priceStatus === "confirmed" ? (
              <CheckIcon width="20" height="20" />
            ) : priceStatus === "rejected" ? (
              <XIcon size={22} />
            ) : (
              <ClockIcon fill="#fff" type={2} width={24} height={24} />
            );

          return (
            <div className={style.row} key={`priceChanged-${i}`}>
              <div className={style.rail}>
                <div className={cn(style.circle, circleCls)}>{circleIcon}</div>
                {!isLast && <div className={style.line} />}
              </div>
              <div className={style.card_wrap}>
                <BidChangeCard
                  item={priceItem}
                  canDecide={canDecideBid}
                  onDone={() => {
                    fetchOrderData?.();
                    onForceUpdate?.();
                  }}
                />
              </div>
            </div>
          );
        }

        const isCompleted = key === "completed";
        const isCanceled = key === "canceled";
        const isRebidClosed = key === "rebidClosed";
        const isCurrent = i === lastStatusBlockIdx && !isCompleted && !isCanceled && !isRebidClosed;
        const isPending = !!pending;
        const isDone = !isCurrent;
        const isLastReturn = key === "return" && i === lastReturnIdx;
        const isRejected = key === "expeditorRejected";
        const isUnloadingModerationCurrent = isCurrent && statusCode === "unloadingcargomoderation";
        const contentOverride = STATUS_CONTENT_OVERRIDES[statusCode];
        const noToggle =
          key === "confirmed" && order?.isWithoutExecutor &&
          (statusCode !== "confirmed" || wagonNumbers.length === 0);
        const open = !noToggle && isOpen(i, isCurrent || isCompleted || isCanceled || isRebidClosed);
        const timeStr = time ? fmtDateTime(time) : null;

        return (
          <div className={style.row} key={`${key}-${i}`}>
            <div className={style.rail}>
              <div
                className={cn(style.circle, {
                  [style.circle_done]: isDone && !isRejected && !isCanceled && !isPending,
                  [style.circle_current]: isCurrent && !isRejected && !isPending && !isUnloadingModerationCurrent,
                  [style.circle_moderationCurrent]: isUnloadingModerationCurrent,
                  [style.circle_rejected]: isRejected || isCanceled,
                  [style.circle_pending]: isPending,
                })}
              >
                {isRejected || isCanceled ? (
                  <XIcon size={22} />
                ) : isPending ? (
                  <ClockIcon fill="#6D7885" type={2} width={24} height={24} />
                ) : isUnloadingModerationCurrent ? (
                  <ClockIcon fill="#fff" type={2} width={24} height={24} />
                ) : (
                  <CheckIcon />
                )}
              </div>
              {!isLast && <div className={style.line} />}
            </div>

            <div className={style.card_wrap}>
              <div className={cn(style.card, { [style.card_current]: isCurrent })}>
                <div
                  className={cn(style.header, { [style.header_open]: open })}
                  onClick={noToggle ? undefined : () => toggle(i, isCurrent)}
                  style={noToggle ? { cursor: "default" } : undefined}
                >
                  <div className={style.header_left}>
                    <span className={style.title}>
                      {t(getStepTitleKey(key, contentOverride, order?.assignmentMethod))}
                    </span>
                    {isCurrent && (
                      <span className={style.now_badge}>
                        <span className={style.now_dot} />
                        <span className={style.now_text}>{t("orderInfoClient.timeline.now")}</span>
                      </span>
                    )}
                  </div>
                  <div className={style.header_right}>
                    {timeStr && <span className={style.time}>{timeStr}</span>}
                    {!noToggle && (
                      <span className={cn(style.chevron, { [style.chevron_open]: open })}>
                        <ChevronIcon />
                      </span>
                    )}
                  </div>
                </div>

                {open && (
                  <div className={style.body}>
                    {renderBody(key, isCurrent, isLastReturn, time, comment, i, moderator, statusCode)}
                  </div>
                )}
              </div>
              {isRejected &&
                isCurrent &&
                (String(order.status).toLowerCase() === "expeditorrejected" || order.status === 19) && (
                  <ReassignExpeditorCard
                    key={`${order.id}-${time || i}`}
                    order={order}
                    fetchOrderData={fetchOrderData}
                    onForceUpdate={onForceUpdate}
                  />
                )}
            </div>
          </div>
        );
      })}

      {showRejectModal && ReactDOM.createPortal(
        <div className={style.modal_overlay} onClick={() => setShowRejectModal(false)} data-portal-modal="true">
          <div className={style.modal} onClick={(e) => e.stopPropagation()}>
            <div className={style.modal_topbar}>
              <h4 className={style.modal_title}>{t("orderInfoClient.timeline.returnModalTitle")}</h4>
              <button
                type="button"
                className={style.modal_close}
                onClick={() => setShowRejectModal(false)}
                aria-label={t("orderInfoClient.timeline.cancel")}
              >
                <XIcon size={24} />
              </button>
            </div>
            <div className={style.modal_field_card}>
              <CustomInput
                labelText={t("orderInfoClient.timeline.returnReasonLabel")}
                placeholder={t("orderInfoClient.timeline.returnReasonPlaceholder")}
                inputType="text"
                isBgGray
                value={rejectComment}
                onChangeValue={(v) => {
                  setRejectComment(v);
                  if (commentError) setCommentError("");
                }}
                errorText={commentError}
              />
            </div>
            <div className={style.modal_actions}>
              <CustomButton
                text={t("orderInfoClient.timeline.cancel")}
                isWhite
                onClick={() => setShowRejectModal(false)}
              />
              <CustomButton
                text={t('assignExecutor.buttons.send')}
                isNavy
                onClick={handleRejectSubmit}
                isLoading={decisionLoading}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default OrderStatusTimeline;
